/**
 * Admin: Payments — manage the sellable catalog (awards tickets + membership
 * renewal tiers) and see incoming Stripe orders.
 *
 * Two tabs:
 *   • Catalog — CRUD payment_products. Every save auto-syncs the row to a
 *     Stripe Product + Price, so it's immediately purchasable at the
 *     public /partners endpoints.
 *   • Orders — read-only list of Stripe checkout sessions, latest first.
 *
 * The Stripe→Xero pipe is configured natively in the Stripe dashboard;
 * this UI only cares about what's on sale and what's been paid.
 */
import React, { useEffect, useState } from 'react';
import { Copy, Edit3, PoundSterling, Plus, RefreshCw, ShoppingBag, Trash2, Users, X } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

function money(pounds, currency = 'gbp') {
  if (pounds == null) return '—';
  const symbol = currency === 'gbp' ? '£' : currency.toUpperCase() + ' ';
  return `${symbol}${Number(pounds).toFixed(2)}`;
}

function moneyPence(pence, currency = 'gbp') {
  if (pence == null) return '—';
  return money(pence / 100, currency);
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

const EMPTY_PRODUCT = {
  category: 'membership',
  name: '',
  description: '',
  tier: 'bronze',
  ticket_type: '',
  amount: 250,
  tier_prices: { nonmember: 95, partner: 89 },
  currency: 'gbp',
  quantity_max: 20,
  active: true,
  sort_order: 99,
};

function ProductEditor({ initial, onCancel, onSave, saving }) {
  const [form, setForm] = useState({
    ...EMPTY_PRODUCT,
    ...(initial || {}),
    tier_prices: { ...EMPTY_PRODUCT.tier_prices, ...(initial?.tier_prices || {}) },
  });
  const isTicket = form.category === 'ticket';

  // Lock background scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function setTierPrice(tier, v) {
    setForm((f) => ({ ...f, tier_prices: { ...(f.tier_prices || {}), [tier]: Number(v) } }));
  }

  return (
    <div data-testid="payment-editor-overlay" className="fixed inset-0 z-[100] bg-charcoal/50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
    <div data-testid="payment-product-editor" className="bg-white border border-charcoal/10 rounded-2xl p-6 space-y-4 w-full max-w-3xl my-auto shadow-2xl">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl">{initial?.id ? 'Edit product' : 'New product'}</h3>
        <button data-testid="payment-editor-close" onClick={onCancel} className="p-1 rounded hover:bg-admin"><X size={18}/></button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="label">Category</span>
          <select value={form.category} onChange={(e) => set('category', e.target.value)} data-testid="payment-category-select"
            className="mt-1 w-full border rounded-xl px-3 py-2 bg-white">
            <option value="membership">Membership renewal</option>
            <option value="ticket">Awards ceremony ticket</option>
          </select>
        </label>
        <label className="block">
          <span className="label">{isTicket ? 'Base price (non-member) £ ex VAT' : 'Price (£ ex VAT)'}</span>
          <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', Number(e.target.value))}
            data-testid="payment-amount-input"
            className="mt-1 w-full border rounded-xl px-3 py-2"/>
        </label>
      </div>

      <label className="block">
        <span className="label">Name (shown to buyer)</span>
        <input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="payment-name-input"
          className="mt-1 w-full border rounded-xl px-3 py-2" placeholder={isTicket ? 'e.g. Awards Ceremony — VIP Ticket' : 'e.g. Silver Membership (annual)'}/>
      </label>

      <label className="block">
        <span className="label">Description (optional)</span>
        <textarea rows={2} value={form.description || ''} onChange={(e) => set('description', e.target.value)} data-testid="payment-description-input"
          className="mt-1 w-full border rounded-xl px-3 py-2"/>
      </label>

      {isTicket && (
        <div className="p-4 bg-admin rounded-xl space-y-3">
          <div className="text-xs uppercase tracking-wide font-bold text-charcoal/70">Ticket pricing (£ ex VAT — 20% added at checkout)</div>
          <div className="text-xs text-charcoal/60 -mt-2">
            Two rates only: non-partners pay the standard price; any signed-in trade partner pays the partner price (regardless of membership level).
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {['nonmember', 'partner'].map((tier) => (
              <label key={tier} className="block">
                <span className="label capitalize">{tier === 'nonmember' ? 'Non-partner' : 'Partner'}</span>
                <input type="number" min="0" step="0.01"
                  value={form.tier_prices?.[tier] ?? form.amount}
                  onChange={(e) => setTierPrice(tier, e.target.value)}
                  data-testid={`payment-tier-price-${tier}`}
                  className="mt-1 w-full border rounded-xl px-3 py-2"/>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        {isTicket ? (
          <label className="block sm:col-span-1">
            <span className="label">Ticket type</span>
            <input value={form.ticket_type || ''} onChange={(e) => set('ticket_type', e.target.value)} data-testid="payment-ticket-type-input"
              placeholder="standard / vip / table_10"
              className="mt-1 w-full border rounded-xl px-3 py-2"/>
          </label>
        ) : (
          <label className="block sm:col-span-1">
            <span className="label">Tier</span>
            <select value={form.tier || 'bronze'} onChange={(e) => set('tier', e.target.value)} data-testid="payment-tier-select"
              className="mt-1 w-full border rounded-xl px-3 py-2 bg-white">
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
            </select>
          </label>
        )}
        <label className="block">
          <span className="label">Max qty / order</span>
          <input type="number" min="1" max="200" value={form.quantity_max} onChange={(e) => set('quantity_max', Number(e.target.value))}
            data-testid="payment-quantity-max-input"
            className="mt-1 w-full border rounded-xl px-3 py-2"/>
        </label>
        <label className="block">
          <span className="label">Sort order</span>
          <input type="number" value={form.sort_order} onChange={(e) => set('sort_order', Number(e.target.value))} data-testid="payment-sort-order-input"
            className="mt-1 w-full border rounded-xl px-3 py-2"/>
        </label>
      </div>

      <label className="flex items-center gap-2">
        <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} data-testid="payment-active-checkbox"/>
        <span>Active — visible to buyers</span>
      </label>

      <div className="flex flex-wrap gap-3 pt-2">
        <button disabled={saving || !form.name || !form.amount}
          onClick={() => onSave(form)}
          data-testid="payment-save-btn"
          className="btn-primary px-5 py-2">
          {saving ? 'Saving…' : (initial?.id ? 'Save changes' : 'Create + sync to Stripe')}
        </button>
        <button onClick={onCancel} data-testid="payment-cancel-btn" className="px-5 py-2 rounded-xl border">Cancel</button>
      </div>
    </div>
    </div>
  );
}

function CatalogTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null); // product doc or {} for new
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');

  async function load() {
    setLoading(true); setErr('');
    try {
      const { data } = await api.get('/admin/payments/products');
      setItems(data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load catalog');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function seedDefaults() {
    setBusy('seed'); setErr('');
    try {
      const { data } = await api.post('/admin/payments/seed', {});
      alert(`Seeded ${data.seeded} defaults`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Seed failed'); }
    finally { setBusy(''); }
  }

  async function save(form) {
    setSaving(true); setErr('');
    try {
      if (editing?.id) {
        await api.put(`/admin/payments/products/${editing.id}`, form);
      } else {
        await api.post('/admin/payments/products', form);
      }
      setEditing(null);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  }

  async function archive(product) {
    if (!window.confirm(`Archive "${product.name}"? Buyers will no longer see it.`)) return;
    setBusy(`archive-${product.id}`); setErr('');
    try {
      await api.delete(`/admin/payments/products/${product.id}`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Archive failed'); }
    finally { setBusy(''); }
  }

  async function resync(product) {
    setBusy(`sync-${product.id}`); setErr('');
    try {
      await api.post(`/admin/payments/products/${product.id}/sync-stripe`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Sync failed'); }
    finally { setBusy(''); }
  }

  if (loading) return <div className="text-charcoal/60">Loading catalog…</div>;

  return (
    <div className="space-y-6">
      <HelpCallout>
        Products edited here are pushed to Stripe automatically. Buyers see them at{' '}
        <code className="px-1 rounded bg-admin">/partners/tourism-awards</code>{' '}
        (tickets) and{' '}
        <code className="px-1 rounded bg-admin">/partners</code>{' '}
        (membership renewal). Prices entered here are{' '}
        <b>exclusive of VAT</b> — 20% VAT is added automatically at checkout.
      </HelpCallout>

      <div className="flex flex-wrap gap-3 items-center">
        <button data-testid="payment-add-product-btn" onClick={() => setEditing({})}
          className="btn-primary px-4 py-2 flex items-center gap-2"><Plus size={16}/> Add product</button>
        {items.length === 0 && (
          <button data-testid="payment-seed-defaults-btn" onClick={seedDefaults} disabled={busy === 'seed'}
            className="px-4 py-2 rounded-xl border flex items-center gap-2">
            {busy === 'seed' ? 'Seeding…' : 'Seed defaults (3 tiers + 3 ticket types)'}
          </button>
        )}
      </div>

      {err && <div data-testid="payment-error" className="p-3 rounded-xl bg-heritage/10 text-heritage text-sm">{err}</div>}

      {editing && (
        <ProductEditor key={editing.id || 'new'} initial={editing.id ? editing : null} onCancel={() => setEditing(null)}
          onSave={save} saving={saving}/>
      )}

      <div className="space-y-3">
        {items.length === 0 && !editing && (
          <div className="text-charcoal/60 py-6">No products yet. Click &quot;Seed defaults&quot; for a sensible starter set or &quot;Add product&quot; for a fresh row.</div>
        )}
        {items.map((p) => (
          <div key={p.id} data-testid={`payment-product-${p.id}`}
            className={`bg-white border rounded-2xl p-4 flex flex-wrap items-start gap-4 ${p.active ? 'border-charcoal/10' : 'border-charcoal/10 opacity-60'}`}>
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${p.category === 'ticket' ? 'bg-heritage/10 text-heritage' : 'bg-forest/10 text-forest'}`}>{p.category}</span>
                {p.tier && <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-admin">{p.tier}</span>}
                {p.ticket_type && <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-admin">{p.ticket_type}</span>}
                {!p.active && <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-charcoal/10 text-charcoal/60">Archived</span>}
                {!p.stripe_price_id && <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">Not synced</span>}
              </div>
              <div className="font-display text-lg mt-1">{p.name}</div>
              {p.description && <div className="text-sm text-charcoal/70 mt-1">{p.description}</div>}
              <div className="text-sm text-charcoal/60 mt-1">
                {p.category === 'ticket' && p.tier_prices ? (
                  <span>
                    Non-partner {money(p.tier_prices.nonmember, p.currency)} · Partner {money(p.tier_prices.partner ?? p.tier_prices.bronze, p.currency)}
                    {' · Max qty ' + p.quantity_max}
                  </span>
                ) : (
                  <span>{money(p.amount, p.currency)} ex VAT · Max qty {p.quantity_max} · Sort {p.sort_order}</span>
                )}
              </div>
              {p.lookup_key && (
                <div className="text-xs text-charcoal/40 mt-1 font-mono flex items-center gap-1">
                  {p.lookup_key}
                  <button data-testid={`payment-copy-lookup-${p.id}`} onClick={() => navigator.clipboard.writeText(p.lookup_key)}
                    className="p-0.5 hover:text-charcoal"><Copy size={12}/></button>
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid={`payment-resync-${p.id}`} onClick={() => resync(p)} disabled={busy === `sync-${p.id}`}
                className="p-2 rounded-xl border hover:bg-admin flex items-center gap-1 text-sm" title="Re-sync to Stripe">
                <RefreshCw size={14} className={busy === `sync-${p.id}` ? 'animate-spin' : ''}/> Sync
              </button>
              <button data-testid={`payment-edit-${p.id}`} onClick={() => setEditing(p)}
                className="p-2 rounded-xl border hover:bg-admin flex items-center gap-1 text-sm">
                <Edit3 size={14}/> Edit
              </button>
              {p.active && (
                <button data-testid={`payment-archive-${p.id}`} onClick={() => archive(p)}
                  className="p-2 rounded-xl border hover:bg-admin flex items-center gap-1 text-sm text-heritage" title="Archive">
                  <Trash2 size={14}/> Archive
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ManualOrderModal({ onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: '', quantity: 1, buyer_name: '', buyer_email: '', invoice_ref: '', amount: '', paid: false, notes: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get('/admin/payments/products?category=ticket').then((r) => {
      const active = (r.data.items || r.data || []).filter((p) => p.active !== false);
      setProducts(active);
      if (active[0]) setForm((f) => ({ ...f, product_id: active[0].id, amount: (active[0].amount * 1).toFixed(2) }));
    }).catch(() => setErr('Could not load ticket types'));
  }, []);
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === 'product_id' || k === 'quantity') {
      const p = products.find((x) => x.id === (k === 'product_id' ? v : f.product_id));
      const qty = Number(k === 'quantity' ? v : f.quantity) || 1;
      if (p) next.amount = (p.amount * qty).toFixed(2);
    }
    return next;
  });
  async function save() {
    if (!form.product_id || !form.buyer_name.trim()) { setErr('Ticket type and buyer name are required.'); return; }
    // Tolerate "mailto:" prefixes pasted from email links on phones
    const email = form.buyer_email.trim().replace(/^mailto:/i, '').split('?')[0].trim();
    setBusy(true); setErr('');
    try {
      await api.post('/admin/payments/manual-order', {
        product_id: form.product_id,
        quantity: Number(form.quantity) || 1,
        buyer_name: form.buyer_name.trim(),
        buyer_email: email || null,
        invoice_ref: form.invoice_ref.trim() || null,
        amount_pounds: form.amount === '' ? null : Number(form.amount),
        paid: form.paid,
        notes: form.notes.trim() || null,
      });
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === 'string') setErr(d);
      else if (Array.isArray(d)) setErr(d.map((x) => `${(x.loc || []).slice(1).join('.')}: ${x.msg}`).join('; ') || 'Could not record the sale');
      else setErr('Could not record the sale');
      setBusy(false);
    }
  }
  return (
    <div data-testid="manual-order-overlay" className="fixed inset-0 z-[100] bg-charcoal/50 flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={onClose}>
      <div data-testid="manual-order-modal" className="bg-white border border-charcoal/10 rounded-2xl p-6 space-y-4 w-full max-w-lg my-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl">Record an invoiced ticket sale</h2>
          <button data-testid="manual-order-close" onClick={onClose} className="p-1 rounded hover:bg-admin"><X size={18}/></button>
        </div>
        <p className="text-xs text-charcoal/60">For partners paying by invoice rather than card. Seats count towards venue capacity straight away, so the ceremony can't be overbooked.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-sm">Ticket type
            <select value={form.product_id} onChange={(e) => set('product_id', e.target.value)} data-testid="manual-order-product-select"
              className="mt-1 w-full border rounded-xl px-3 py-2 bg-white">
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {money(p.amount)}</option>)}
            </select>
          </label>
          <label className="text-sm">Quantity
            <input type="number" min="1" max="50" value={form.quantity} onChange={(e) => set('quantity', e.target.value)}
              data-testid="manual-order-quantity-input" className="mt-1 w-full border rounded-xl px-3 py-2"/>
          </label>
          <label className="text-sm">Invoice total (£)
            <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)}
              data-testid="manual-order-amount-input" className="mt-1 w-full border rounded-xl px-3 py-2"/>
          </label>
          <label className="col-span-2 text-sm">Buyer / organisation *
            <input value={form.buyer_name} onChange={(e) => set('buyer_name', e.target.value)}
              data-testid="manual-order-buyer-input" placeholder="e.g. Leonardo Hinckley Island Hotel"
              className="mt-1 w-full border rounded-xl px-3 py-2"/>
          </label>
          <label className="text-sm">Buyer email
            <input type="email" value={form.buyer_email} onChange={(e) => set('buyer_email', e.target.value)}
              data-testid="manual-order-email-input" className="mt-1 w-full border rounded-xl px-3 py-2"/>
          </label>
          <label className="text-sm">Invoice reference
            <input value={form.invoice_ref} onChange={(e) => set('invoice_ref', e.target.value)}
              data-testid="manual-order-invoice-ref-input" placeholder="e.g. INV-2026-041"
              className="mt-1 w-full border rounded-xl px-3 py-2"/>
          </label>
          <label className="col-span-2 text-sm">Notes
            <input value={form.notes} onChange={(e) => set('notes', e.target.value)}
              data-testid="manual-order-notes-input" placeholder="Optional — dietary needs, table requests…"
              className="mt-1 w-full border rounded-xl px-3 py-2"/>
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.paid} onChange={(e) => set('paid', e.target.checked)} data-testid="manual-order-paid-checkbox"/>
          Invoice already settled — record as paid
        </label>
        {err && <div data-testid="manual-order-error" className="p-2.5 rounded-xl bg-heritage/10 text-heritage text-sm">{err}</div>}
        <div className="flex gap-3">
          <button onClick={save} disabled={busy} data-testid="manual-order-save-btn"
            className="px-5 py-2 rounded-xl bg-heritage text-cream font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Record sale'}
          </button>
          <button onClick={onClose} data-testid="manual-order-cancel-btn" className="px-5 py-2 rounded-xl border">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function OrdersTab() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [capacityInput, setCapacityInput] = useState('');
  useEffect(() => { if (summary?.capacity) setCapacityInput(String(summary.capacity)); }, [summary]);
  async function saveCapacity() {
    await api.put('/admin/site-settings/awards_capacity', { value: { seats: parseInt(capacityInput || '0', 10) || 0 } });
    load();
  }
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [err, setErr] = useState('');

  async function load() {
    setLoading(true); setErr('');
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    try {
      const [ordersRes, summaryRes] = await Promise.all([
        api.get(`/admin/payments/orders${params.toString() ? `?${params}` : ''}`),
        api.get('/admin/payments/orders/summary'),
      ]);
      setItems(ordersRes.data.items || []);
      setSummary(summaryRes.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to load orders'); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [status, category]);

  return (
    <div className="space-y-4">
      {summary && (
        <div data-testid="ticket-sales-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Ticket revenue', value: moneyPence(summary.revenue_pence), testid: 'summary-revenue' },
            { label: 'Awaiting invoice', value: `${moneyPence(summary.invoiced_pence || 0)}${summary.invoiced_orders ? ` (${summary.invoiced_orders})` : ''}`, testid: 'summary-invoiced' },
            { label: 'Paid orders', value: summary.paid_orders, testid: 'summary-orders' },
            { label: 'Tickets sold', value: summary.tickets_sold, testid: 'summary-tickets' },
            { label: 'Seats sold', value: summary.seats_sold, testid: 'summary-seats' },
            { label: 'Seats remaining', value: summary.capacity ? `${summary.seats_remaining} of ${summary.capacity}` : 'Set capacity →', testid: 'summary-remaining' },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-white border border-charcoal/10 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-wide text-charcoal/50 font-bold">{s.label}</div>
              <div className="font-display text-2xl mt-1">{s.value}</div>
            </div>
          ))}
          <div className="col-span-2 sm:col-span-4 flex flex-wrap items-center gap-2 text-xs text-charcoal/50 -mt-1">
            <span>Awards tickets only. Seats count a Table of 10 as 10 and include invoiced (offline) sales so the room can&apos;t be overbooked. Refunds made in Stripe sync automatically (checked every 10 minutes when you open this tab). When a booking would exceed remaining seats, buyers are offered the waiting list. Note: capacity 0 means unlimited (no sold-out guard).</span>
            <span className="inline-flex items-center gap-1.5 ml-auto">
              Venue capacity:
              <input type="number" min="0" value={capacityInput}
                onChange={(e) => setCapacityInput(e.target.value)}
                data-testid="awards-capacity-input"
                className="w-20 border rounded-lg px-2 py-1 bg-white text-charcoal" />
              <button data-testid="awards-capacity-save" onClick={saveCapacity}
                className="px-2.5 py-1 rounded-lg bg-charcoal text-cream font-semibold">Save</button>
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-3 items-center">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="payment-orders-status-filter"
          className="border rounded-xl px-3 py-2 bg-white">
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="invoiced">Invoiced</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="expired">Expired</option>
          <option value="refunded">Refunded</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="payment-orders-category-filter"
          className="border rounded-xl px-3 py-2 bg-white">
          <option value="">All categories</option>
          <option value="ticket">Tickets</option>
          <option value="membership">Membership</option>
        </select>
        <button data-testid="payment-orders-refresh-btn" onClick={load} className="px-4 py-2 rounded-xl border flex items-center gap-2">
          <RefreshCw size={14}/> Refresh
        </button>
        <button data-testid="record-invoiced-sale-btn" onClick={() => setShowManual(true)}
          className="px-4 py-2 rounded-xl bg-heritage text-cream font-semibold flex items-center gap-2 ml-auto">
          <Plus size={14}/> Record invoiced sale
        </button>
      </div>
      {showManual && <ManualOrderModal onClose={() => setShowManual(false)} onSaved={() => { setShowManual(false); load(); }}/>}

      <p className="text-xs text-charcoal/50" data-testid="orders-status-legend">
        <strong>Pending</strong> = checkout opened but payment not confirmed yet — re-checked with Stripe every time this tab loads, then flips to Paid (you get an alert) or Expired (abandoned, no money taken). You're only alerted when a payment actually completes.
      </p>

      {err && <div className="p-3 rounded-xl bg-heritage/10 text-heritage text-sm">{err}</div>}
      {loading && <div className="text-charcoal/60">Loading…</div>}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-charcoal/10 rounded-2xl overflow-hidden">
          <thead className="bg-admin text-left text-xs uppercase tracking-wide text-charcoal/60">
            <tr>
              <th className="p-3">Date</th>
              <th className="p-3">Product</th>
              <th className="p-3">Buyer</th>
              <th className="p-3">Qty</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.session_id} data-testid={`payment-order-${o.session_id}`}
                className="border-t border-charcoal/5 text-sm">
                <td className="p-3 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                <td className="p-3">
                  <div className="font-medium">{o.product_name}</div>
                  <div className="text-xs text-charcoal/50">{o.category}{o.payment_method === 'invoice' ? ' · offline sale' : ''}</div>
                  {o.invoice_ref && <div className="text-xs text-indigo-700 font-medium">{o.invoice_ref}</div>}
                </td>
                <td className="p-3">
                  <div>{o.buyer_name || o.customer_email || '—'}</div>
                  <div className="text-xs text-charcoal/50">{o.buyer_email || o.customer_email || ''}</div>
                  {(o.attendee_names || []).length > 0 && (
                    <div className="text-xs text-forest mt-0.5" title={o.attendee_names.join(', ')} data-testid={`order-attendees-${o.session_id}`}>
                      Guests: {o.attendee_names.join(', ')}
                    </div>
                  )}
                  {o.notes && <div className="text-xs text-charcoal/50 italic mt-0.5">{o.notes}</div>}
                </td>
                <td className="p-3">{o.quantity}</td>
                <td className="p-3">{moneyPence(o.amount_total ?? o.amount, o.currency)}</td>
                <td className="p-3">
                  <span title={
                    o.payment_status === 'pending' ? 'The buyer opened Stripe checkout but payment has not been confirmed. We re-check Stripe every time this tab loads — it flips to Paid automatically if they paid, or Expired once the checkout lapses (24h).' :
                    o.payment_status === 'expired' ? 'The buyer opened checkout but never paid — the Stripe session expired after 24 hours. No money was taken.' :
                    o.payment_status === 'failed' ? 'Stripe could not take the payment (card declined or similar). No money was taken.' : ''
                  } className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${
                    o.payment_status === 'paid' ? 'bg-forest/10 text-forest' :
                    o.payment_status === 'invoiced' ? 'bg-indigo-100 text-indigo-800' :
                    o.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    o.payment_status === 'refunded' ? 'bg-charcoal/10 text-charcoal/60' :
                    o.payment_status === 'partially_refunded' ? 'bg-orange-100 text-orange-800' :
                    'bg-heritage/10 text-heritage'
                  }`}>{o.payment_status}</span>
                  {o.payment_status === 'paid' && (
                    <div className="mt-1">
                      {o.confirmation_email?.status === 'sent' && <span data-testid={`conf-email-sent-${o.session_id}`} className="block text-[10px] text-forest font-semibold" title={`Sent to ${o.confirmation_email.to}`}>✓ confirmation sent</span>}
                      {o.confirmation_email?.status === 'failed' && <span data-testid={`conf-email-failed-${o.session_id}`} className="block text-[10px] text-heritage font-bold" title={o.confirmation_email.error || ''}>✗ confirmation failed</span>}
                      <button onClick={async () => {
                          if (!window.confirm(`Re-send the booking confirmation to ${o.buyer_email || o.customer_email || 'the buyer'}?`)) return;
                          try { const r = await api.post(`/admin/payments/orders/${o.session_id}/resend-confirmation`); alert(`Confirmation sent to ${r.data.to}`); load(); }
                          catch (ex) { alert(ex.response?.data?.detail || 'Send failed'); }
                        }}
                        data-testid={`resend-confirmation-${o.session_id}`}
                        className="block text-[10px] underline text-charcoal/50 hover:text-charcoal">
                        {o.confirmation_email ? 'Re-send confirmation' : 'Send confirmation email'}
                      </button>
                    </div>
                  )}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {o.payment_status === 'invoiced' && (
                    <button onClick={async () => { await api.post(`/admin/payments/manual-order/${o.session_id}/mark-paid`); load(); }}
                      data-testid={`order-mark-paid-${o.session_id}`} title="Invoice settled — mark as paid"
                      className="px-2.5 py-1 mr-1 rounded-lg bg-forest/10 text-forest text-xs font-semibold hover:bg-forest/20">Mark paid</button>
                  )}
                  <button onClick={async () => { if (window.confirm(`Delete this ${o.payment_status} order (${o.product_name}, qty ${o.quantity})? This only removes the record here — it does not refund anything in Stripe.`)) { await api.delete(`/admin/payments/orders/${o.session_id}`); load(); } }}
                    data-testid={`order-delete-${o.session_id}`} title="Delete order record"
                    className="p-1.5 rounded-lg hover:bg-stone text-heritage"><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-charcoal/50">No orders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const [tab, setTab] = useState('catalog');
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <PoundSterling className="text-heritage" size={28}/>
        <h1 className="font-display text-3xl">Payments</h1>
      </div>
      <div className="flex gap-2 border-b border-charcoal/10">
        <button data-testid="payments-tab-catalog" onClick={() => setTab('catalog')}
          className={`px-4 py-2 -mb-px border-b-2 ${tab === 'catalog' ? 'border-heritage text-heritage font-medium' : 'border-transparent text-charcoal/60'}`}>
          <span className="flex items-center gap-2"><PoundSterling size={16}/> Catalog</span>
        </button>
        <button data-testid="payments-tab-orders" onClick={() => setTab('orders')}
          className={`px-4 py-2 -mb-px border-b-2 ${tab === 'orders' ? 'border-heritage text-heritage font-medium' : 'border-transparent text-charcoal/60'}`}>
          <span className="flex items-center gap-2"><ShoppingBag size={16}/> Orders</span>
        </button>
        <button data-testid="payments-tab-waitlist" onClick={() => setTab('waitlist')}
          className={`px-4 py-2 -mb-px border-b-2 ${tab === 'waitlist' ? 'border-heritage text-heritage font-medium' : 'border-transparent text-charcoal/60'}`}>
          <span className="flex items-center gap-2"><Users size={16}/> Waiting list</span>
        </button>
      </div>
      {tab === 'catalog' ? <CatalogTab/> : tab === 'orders' ? <OrdersTab/> : <WaitlistTab/>}
    </div>
  );
}

function WaitlistTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  async function load() {
    try { setData((await api.get('/admin/payments/waitlist')).data); }
    catch { setErr('Could not load the waiting list'); }
  }
  useEffect(() => { load(); }, []);
  async function setStatus(id, status) {
    await api.patch(`/admin/payments/waitlist/${id}`, { status }); load();
  }
  async function remove(id) {
    if (!window.confirm('Remove this person from the waiting list?')) return;
    await api.delete(`/admin/payments/waitlist/${id}`); load();
  }
  if (err) return <div className="p-4 rounded-xl bg-heritage/10 text-heritage">{err}</div>;
  if (!data) return <div className="text-charcoal/60">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="payments-waitlist-panel">
      <div className="text-sm text-charcoal/70">
        <strong data-testid="waitlist-waiting-count">{data.items.filter((i) => i.status === 'waiting').length}</strong> waiting ·{' '}
        <strong>{data.waiting_tickets}</strong> tickets requested in total. Oldest first — contact in order when seats free up.
      </div>
      {data.items.length === 0 && <div className="p-6 rounded-xl bg-white border border-charcoal/10 text-charcoal/60">Nobody on the waiting list yet.</div>}
      {data.items.length > 0 && (
        <div className="bg-white border border-charcoal/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-charcoal/50 border-b border-charcoal/10">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Tickets</th>
              <th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {data.items.map((w) => (
                <tr key={w.id} className="border-b border-charcoal/5" data-testid={`waitlist-row-${w.id}`}>
                  <td className="px-4 py-3 font-medium">{w.name}</td>
                  <td className="px-4 py-3">{w.company || '—'}</td>
                  <td className="px-4 py-3 font-semibold">{w.tickets_requested}</td>
                  <td className="px-4 py-3"><a href={`mailto:${w.email}`} className="underline">{w.email}</a></td>
                  <td className="px-4 py-3">{w.phone}</td>
                  <td className="px-4 py-3 text-charcoal/60">{(w.created_at || '').slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${w.status === 'contacted' ? 'bg-forest/15 text-forest' : 'bg-mustard/30 text-charcoal'}`}>{w.status}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap space-x-2">
                    {w.status === 'waiting'
                      ? <button onClick={() => setStatus(w.id, 'contacted')} data-testid={`waitlist-contacted-${w.id}`} className="text-xs px-2 py-1 rounded-lg border border-forest/40 text-forest">Mark contacted</button>
                      : <button onClick={() => setStatus(w.id, 'waiting')} data-testid={`waitlist-waiting-${w.id}`} className="text-xs px-2 py-1 rounded-lg border text-charcoal/60">Back to waiting</button>}
                    <button onClick={() => remove(w.id)} data-testid={`waitlist-remove-${w.id}`} className="text-xs px-2 py-1 rounded-lg border border-heritage/40 text-heritage">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
